import React, { useState } from "react";
import { TID } from "@/constants/tids";
import logo from "@/assets/logo";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import AuthDialog from "@/components/AuthDialog";
import SupportDialog from "@/components/SupportDialog";
import {
  Sparkles,
  TrendingUp,
  Filter,
  ListChecks,
  HeartPulse,
  Activity,
  Users,
  Network,
  Database,
  MessageSquare,
  FileDown,
  ShieldCheck,
  Lock,
  Zap,
  Clock,
  KeyRound,
  ArrowRight,
  Sun,
  Moon,
  Target,
  BarChart3,
  CheckCircle2,
  LogIn,
} from "lucide-react";

const FEATURES = [
  { icon: Sparkles, title: "AI Sales Analysis", desc: "An executive-grade written report on pipeline health, win/loss, rep performance, risk and recommendations — authored by a senior-analyst AI (Claude Sonnet 4.5)." },
  { icon: TrendingUp, title: "Forecast", desc: "Forward-looking, probability-weighted forecast by month & quarter, with an editable quota to see gap-to-goal and pipeline coverage." },
  { icon: Filter, title: "Pipeline Funnel", desc: "True stage-to-stage conversion, drop-off and velocity built from each deal's stage history — find exactly where deals stall." },
  { icon: ListChecks, title: "Deal Risk Scoring", desc: "Your full book of deals with a 0–100 risk score on every open deal (age, overdue, inactivity, low win probability) — searchable & sortable." },
  { icon: HeartPulse, title: "Data Health", desc: "A CRM hygiene scorecard flagging missing amounts, unassigned deals, overdue closes and stale records so you can clean up fast." },
  { icon: Activity, title: "Activity Insights", desc: "Rep activity mix and trends across calls, emails, meetings, tasks and notes — see who's active and what's driving wins." },
  { icon: Users, title: "Reps / Team", desc: "Per-rep scorecards: wins, win rate, average cycle time, activity and activities-per-win — coach with real numbers." },
  { icon: Network, title: "Relationship Mapping", desc: "Cluster contacts into accounts by company and shared email domain to reveal your real relationship map." },
  { icon: Database, title: "CRM Inventory", desc: "A scope-aware inventory of every CRM object your key can read — companies, leads, quotes, orders and more — with live counts." },
  { icon: MessageSquare, title: "Query Data (AI Q&A)", desc: "Ask plain-English questions about your pipeline, reps, forecast and deals — answers grounded in your synced data." },
  { icon: FileDown, title: "Branded PDF & Print", desc: "Export any view as a polished, SalesTap-branded PDF with header logo and page footers — ready to share with leadership." },
  { icon: Target, title: "Period Filtering", desc: "Slice closed-deal and activity metrics by any period while open-pipeline and risk stay accurate as-of-now." },
];

const STEPS = [
  { n: 1, icon: KeyRound, title: "Connect", desc: "Paste a read-only HubSpot Private App token, Service Key, or Personal Access Key — or upload a CSV. We auto-detect it." },
  { n: 2, icon: Zap, title: "Sync", desc: "SalesTap securely pulls your deals, owners, pipelines, contacts and activities into a private, temporary workspace." },
  { n: 3, icon: BarChart3, title: "Analyze", desc: "Explore dashboards, deep-dives and an AI executive report in seconds — then export or ask follow-up questions." },
];

const USE_CASES = [
  { icon: Target, title: "Sales Leaders & VPs", desc: "Walk into every forecast call with a defensible, weighted number, clear risk flags, and an executive summary you can forward." },
  { icon: BarChart3, title: "RevOps & Sales Ops", desc: "Audit pipeline hygiene, conversion bottlenecks and data quality in minutes — no dashboards to build or maintain." },
  { icon: Users, title: "Managers & Reps", desc: "Coach with per-rep scorecards and activity insights; reps see their stalled deals and what to work next." },
];

const BENEFITS = [
  { icon: Zap, title: "Insights in seconds", desc: "No dashboards to build. Connect and get a full analytical deep-dive immediately." },
  { icon: Lock, title: "Read-only & secure", desc: "We never see your password. Access is read-only and used only to fetch your data for analysis." },
  { icon: Clock, title: "Ephemeral by design", desc: "Your data lives in a private session and auto-expires — nothing is retained after you leave." },
  { icon: ShieldCheck, title: "No integration setup", desc: "No lengthy OAuth app install or IT ticket — a read-only key (or CSV) is all you need." },
];

// A lightweight, brand-styled analytics "product" graphic (pure SVG/markup — no
// stock photos), representing SalesTap's KPI + funnel + forecast visuals.
function HeroVisual() {
  return (
    <div className="relative w-full max-w-md mx-auto">
      <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full opacity-60" aria-hidden="true" />
      <div className="relative rounded-2xl border border-border bg-card shadow-2xl p-5 space-y-4">
        {/* KPI row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { l: "Open Pipeline", v: "$1.24M" },
            { l: "Weighted", v: "$612K" },
            { l: "Win Rate", v: "38%" },
          ].map((k) => (
            <div key={k.l} className="rounded-lg border border-border bg-background px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.l}</p>
              <p className="text-base font-bold text-foreground">{k.v}</p>
            </div>
          ))}
        </div>
        {/* Bar chart */}
        <div className="rounded-lg border border-border bg-background p-3">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Forecast by month</p>
          <div className="flex items-end gap-2 h-24">
            {[40, 62, 48, 80, 70, 95].map((h) => (
              <div key={`bar-${h}`} className="flex-1 rounded-t bg-primary/80" style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>
        {/* Funnel */}
        <div className="rounded-lg border border-border bg-background p-3 space-y-1.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Pipeline funnel</p>
          {[100, 72, 45, 28].map((w) => (
            <div key={`funnel-${w}`} className="h-4 rounded bg-primary/70" style={{ width: `${w}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Landing({ onLogin, onRegister, theme, onToggleTheme }) {
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const [resetOpen, setResetOpen] = useState(false); // public "forgot password" support form
  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };
  // "Forgot password?" → close the auth dialog and open the public Support form
  // pre-set to a Password Reset request (Support-assisted reset flow).
  const onForgotPassword = () => {
    setAuthOpen(false);
    setResetOpen(true);
  };
  // The "Connect your HubSpot" CTAs prompt sign-up (registration required first).
  const onGetStarted = () => openAuth("register");
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid={TID.landingRoot}>
      {/* Marketing header */}
      <header className="sticky top-0 z-20 bg-background/80 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={logo} alt="SalesTap logo" className="w-9 h-9 object-contain" />
            <span className="font-bold text-lg text-foreground">SalesTap</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid={TID.landingThemeToggle}
              onClick={onToggleTheme}
              variant="outline"
              size="icon"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button data-testid="landing-login-btn" variant="outline" onClick={() => openAuth("login")}>
              <LogIn className="w-4 h-4 mr-1.5" />
              Log in
            </Button>
            <Button data-testid={TID.landingCtaHeader} onClick={() => openAuth("register")}>
              Sign up
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </header>

      <AuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        mode={authMode}
        onModeChange={setAuthMode}
        onLogin={onLogin}
        onRegister={onRegister}
        onForgotPassword={onForgotPassword}
      />

      {/* Support-assisted password reset (no email service): opens the public
          Support form pre-set to a Password Reset request. */}
      <SupportDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        mode="public"
        presetCategory="password_reset"
        presetSubject="Password reset request"
        presetMessage="I've forgotten my password and can't log in. Please help me reset it for this email address."
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" aria-hidden="true" />
          <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-medium text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-full mb-5">
                <Sparkles className="w-3.5 h-3.5" />
                AI-powered sales analytics for HubSpot
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground leading-[1.1]">
                Your senior sales analyst,{" "}
                <span className="text-primary">on tap.</span>
              </h1>
              <p className="mt-5 text-lg text-muted-foreground max-w-xl">
                Connect your HubSpot CRM and get an instant, executive-grade deep-dive on your
                pipeline, win rates, rep performance and forecast — complete with a written analysis
                by an AI senior analyst. No dashboards to build. No integration to install.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button data-testid={TID.landingCtaPrimary} size="lg" onClick={onGetStarted} className="text-base">
                  <KeyRound className="w-5 h-5 mr-2" />
                  Connect your HubSpot
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <span className="inline-flex items-center gap-2 text-sm text-primary bg-primary/10 border border-primary/20 px-3 py-2 rounded-full">
                  <Lock className="w-4 h-4" />
                  Read-only &amp; secure — we never see your password
                </span>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                No credential handy? You can also upload a CSV export — analysis works either way.
              </p>
            </div>
            <HeroVisual />
          </div>
        </section>

        {/* Purpose / What it is */}
        <section className="max-w-4xl mx-auto px-6 py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">
            Turn your CRM into decisions — not spreadsheets.
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            SalesTap reads your HubSpot data and does the analytical heavy lifting for you: it
            computes the KPIs, builds the funnel, scores deal risk, grades data hygiene, and writes
            a leadership-ready narrative — so you spend time acting on insights, not assembling them.
          </p>
        </section>

        {/* Features */}
        <section className="bg-card/50 border-y border-border">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Everything you need to analyze sales</h2>
              <p className="mt-3 text-muted-foreground">One connection unlocks a full suite of deep-dive analytics.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="landing-features">
              {FEATURES.map((f) => (
                <div key={f.title} className="rounded-xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-lg transition">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <f.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">From connection to insight in three steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="relative rounded-xl border border-border bg-card p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center">{s.n}</span>
                  <s.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-lg">{s.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Use cases */}
        <section className="bg-card/50 border-y border-border">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">Built for every sales role</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {USE_CASES.map((u) => (
                <div key={u.title} className="rounded-xl border border-border bg-card p-6">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <u.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground text-lg">{u.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{u.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">Why teams choose SalesTap</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-xl border border-border bg-card p-5 text-center">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <b.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground">{b.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Security band */}
        <section className="bg-card/50 border-y border-border">
          <div className="max-w-4xl mx-auto px-6 py-14">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground">Security &amp; privacy, by design</h2>
                <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                  {[
                    "Read-only access — SalesTap never modifies your CRM and never sees your password.",
                    "Your credential is used only to fetch data for analysis, over encrypted connections.",
                    "Data is stored ephemerally in a private per-session workspace and auto-expires (~90 min).",
                    "Disconnect anytime to clear everything instantly. Nothing is retained after you leave.",
                  ].map((t) => (
                    <li key={t} className="flex gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-5xl mx-auto px-6 py-20 text-center">
          <img src={logo} alt="SalesTap logo" className="w-16 h-16 object-contain mx-auto mb-5" />
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
            See your pipeline like a senior analyst would.
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Connect your HubSpot in under a minute and get your first executive-grade analysis today.
          </p>
          <div className="mt-8 flex justify-center">
            <Button data-testid={TID.landingCtaFinal} size="lg" onClick={onGetStarted} className="text-base">
              <KeyRound className="w-5 h-5 mr-2" />
              Connect your HubSpot
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">Read-only &amp; secure · or upload a CSV instead</p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
